// libraries
import React, { useEffect, useState } from 'react'
import clsx from 'clsx'

// icons
import { CaretDown } from '../Svg/Others/Icons'

interface BreadcrumbsProps {
	className?: string
	breadcrumbs: Array<{
		name: string
		href?: string
	}>
}

export default function Breadcrumbs({
	className,
	breadcrumbs
}: BreadcrumbsProps) {

	const [breadcrumbColor, setBreadcrumbColor] = useState('transparent')

	// convert relative URLs to absolute URLs if needed
	// server-side this runs under Nashorn, where there is no window: an item
	// that cannot be made absolute is left out rather than emitted relative
	const toAbsoluteUrl = (href: string) => {
		if (href.startsWith('http')) {
			return href
		}

		if (typeof window === 'undefined') {
			return null
		}

		// a relative path without leading slash still needs one
		return href.startsWith('/')
			? `${window.location.origin}${href}`
			: `${window.location.origin}/${href}`
	}

	// json-ld data for breadcrumbs
	const generateStructuredData = () => {
		const structuredData = {
			"@context": "https://schema.org",
			"@type": "BreadcrumbList",
			"itemListElement": breadcrumbs.map((item, index) => {
				const listItem: any = {
					"@type": "ListItem",
					"position": index + 1,
					"name": item.name
				}
				
				// only include 'item' field if we have a valid URL (not # or empty)
				// current page (last item) typically doesn't have href
				if (item.href && item.href !== '#' && item.href.trim() !== '') {
					const itemUrl = toAbsoluteUrl(item.href)

					if (itemUrl) {
						listItem.item = itemUrl
					}
				}
				
				return listItem
			})
		}

		// same escape as serialiseJsonLd in lib/modules/structured-data: a name
		// containing </script> would otherwise close the tag early
		return JSON.stringify(structuredData).split('</').join('<\\/')
	}

	useEffect(() => {
		const updateBreadcrumbColor = () => {
			const sections = document.querySelectorAll('section')
			let currentBgColor = ''

			sections.forEach(section => {
				const rect = section.getBoundingClientRect()
				const isMobile = window.matchMedia('(max-width: 767px)').matches
				const measure = isMobile ? 123 : 162

				if (rect.top <= measure && rect.bottom >= 0) {
					const sectionBgColor = window.getComputedStyle(section).backgroundColor

					if (sectionBgColor !== 'rgba(0, 0, 0, 0)' && sectionBgColor !== 'transparent') {
						currentBgColor = sectionBgColor
					}
				}
			})

			// ff no valid section background color is found, check <main>
			if (!currentBgColor || currentBgColor === 'rgba(0, 0, 0, 0)' || currentBgColor === 'transparent') {
				const mainElement = document.getElementById('main')

				if (mainElement) {
					const mainBgColor = window.getComputedStyle(mainElement).backgroundColor

					if (mainBgColor !== 'rgba(0, 0, 0, 0)' && mainBgColor !== 'transparent') {
						currentBgColor = mainBgColor
					}
				}
			}
	
			// if no valid background is found in <main>, check <body>
			if (!currentBgColor || currentBgColor === 'rgba(0, 0, 0, 0)' || currentBgColor === 'transparent') {
				const bodyBgColor = window.getComputedStyle(document.body).backgroundColor

				currentBgColor = bodyBgColor !== 'rgba(0, 0, 0, 0)' && bodyBgColor !== 'transparent'
					? bodyBgColor
					: 'transparent'
			}
	
			// apply the determined background color
			setBreadcrumbColor(currentBgColor)
		}

		const nextSectionColor = () => {
			const sections = document.querySelectorAll('section')
			let nextSectionIndex = 0

			Array.from(sections).forEach((section, index) => {
				const rect = section.getBoundingClientRect()

				if (rect.top <= 0) {
					nextSectionIndex = index + 1
				}
			})

			if (nextSectionIndex < sections.length) {
				const nextSection = sections[nextSectionIndex]
				const computedStyle = window.getComputedStyle(nextSection)
				const currentBgColor = computedStyle.backgroundColor
				setBreadcrumbColor(currentBgColor)
			} else {
				setBreadcrumbColor('transparent')
			}
		}

		window.addEventListener('scroll', updateBreadcrumbColor)
		// updateBreadcrumbColor();

		let lastScrollTop = 0
    
		const handleScroll = () => {
			const breadcrumbs = document.querySelector('.breadcrumbs__section')
			if (!breadcrumbs) return

			// show / hide header based on scroll direction
			const isMobile = window.matchMedia('(max-width: 767px)').matches
			let currentScroll = 0

			if (isMobile) {
				currentScroll = window.pageYOffset || document.documentElement.scrollTop
			}

			// scrolling down / up
			if (currentScroll > lastScrollTop) {
				breadcrumbs.classList.add('breadcrumbs__section--hidden')
			} else {
				breadcrumbs.classList.remove('breadcrumbs__section--hidden')
			}

			// prevent negative scroll values
			lastScrollTop = currentScroll <= 0 ? 0 : currentScroll
		}

		window.addEventListener('scroll', handleScroll)

	}, [])

	const rgbToHex = (rgb: string) => {
		const rgbValues = rgb.match(/\d+/g)
		return rgbValues ? `#${rgbValues.map(x => parseInt(x).toString(16).padStart(2, '0')).join('')}`.toUpperCase() : ''
	}

	const isActiveColor = [
		'#001F42',
		'#002855',
		'rgb(0, 31, 66)'
	].includes(breadcrumbColor) || ['#001F42', '#002855'].includes(rgbToHex(breadcrumbColor))

	useEffect(() => {
		const dropdownIcons = document.querySelectorAll('.breadcrumbs__dropdown-icon')

		dropdownIcons.forEach(icon => {
			icon.addEventListener('click', handleDropdownClick)
		})

		return () => {
			dropdownIcons.forEach(icon => {
				icon.removeEventListener('click', handleDropdownClick)
			})
		}
	}, [])

	function handleDropdownClick(event) {
		const dropdownIcon = event.currentTarget
		const dropdown = dropdownIcon.closest('.breadcrumbs__list')
		const dropdownArea = dropdown.querySelector('.breadcrumbs__dropdown-area')
	
		dropdownArea.classList.toggle('breadcrumbs__dropdown-area--show')
		dropdownIcon.setAttribute('aria-expanded', dropdownArea.classList.contains('breadcrumbs__dropdown-area--show') ? 'true' : 'false')
	}

	return breadcrumbs.length > 0 ? (
		<>
			{/* json-ld data */}
			<script 
				type='application/ld+json'
				dangerouslySetInnerHTML={{ __html: generateStructuredData() }}
			/>
			
			<section className='section breadcrumbs__section'>
				<div className='breadcrumbs__color' style={{ backgroundColor: breadcrumbColor }}>
					<div className={clsx('grid-container')}>
						<div className='grid-row'>
							<div className='c-12'>

								<nav className={clsx('breadcrumbs', isActiveColor && 'breadcrumbs--light', className)} aria-label='Breadcrumb'>

									<ol className='breadcrumbs__list'>

										{/* this is the dropdown for mobile */}
										<li className='breadcrumbs__dropdown'>
											<span className='breadcrumbs__dropdown-wrapper'>
												
												<span className='text-light-bg-tertiary'>
													...
												</span>

												<button className='breadcrumbs__dropdown-icon' aria-expanded='false' aria-label='Show more breadcrumbs'>
													<CaretDown />
												</button>

											</span>
										</li>

										{/* this is the list in mobile, that renders the list, except not the last li */}
										<li className='breadcrumbs__dropdown-area'>
											{breadcrumbs.map((item, i) => (
												<span className='li breadcrumbs__mobile-item' key={`mobile-${i}`}>
													{item.href ? (
														<a href={item.href} className='regular-link'>
															{item.name}
														</a>
													) : (
														<span aria-current='page'>
															{item.name}
														</span>
													)}

													{i < breadcrumbs.length - 1 && (
														<span className='breadcrumbs__separator' aria-hidden='true'>/</span>
													)}
												</span>
											))}
										</li>

										{/* this is the desktop version */}
										{breadcrumbs.map((item, i) => (
											<li className='breadcrumbs__item' key={`desktop-${i}`}>
												{item.href ? (
													<a href={item.href} className='regular-link'>
														{item.name}
													</a>
												) : (
													<span aria-current='page'>
														{item.name}
													</span>
												)}

												{i < breadcrumbs.length - 1 && (
													<span className='breadcrumbs__separator' aria-hidden='true'>/</span>
												)}
											</li>
										))}

									</ol>

								</nav>

							</div>
						</div>
					</div>
				</div>
			</section>
		</>
	) : <></>
}